import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { UrlHelper } from '../../../utils/url-helper';
import { useTimelineData } from './useTimelineData';
import { processPrimitivesHorizontal } from '../utils/timeline-processing';
import { QUERY_KEYS, CACHE_TIMES } from '../constants';
import { PrimitiveName } from '../utils/constants';
import { WebsiteViewService } from '../../../model/WebsiteViewService';
import { 
  NamespaceGroup,
  MetricsApiResponse, 
  Metric
} from '../types/metrics.types';

export const usePrimitiveMetricsData = () => {
  useLocation(); // This makes the hook reactive to URL changes
  const website = UrlHelper.getParamValue('website');

  // Fetch timeline data
  const { data: timelineData, isLoading: timelineLoading } = useTimelineData(website || '');

  // Extract unique primitives from timeline data
  const uniquePrimitives = React.useMemo(() => {
    if (!timelineData?.primitives) return [];
    
    // Use the same processing logic as timeline to maintain consistent ordering
    const processedPipelines = processPrimitivesHorizontal(timelineData.primitives);
    const orderedPrimitives: string[] = [];
    const seen = new Set<string>();

    const EXCLUDED_PRIMITIVES = new Set<string>([PrimitiveName.DTP, PrimitiveName.SCI_DELTA, PrimitiveName.DTP_DELTA, PrimitiveName.SDO_ThirdParty]);

    // Extract primitive names in the same order as they appear in processed pipelines
    processedPipelines.forEach(pipeline => {
      pipeline.steps.forEach(step => {
        if (step.name && !seen.has(step.name) && !EXCLUDED_PRIMITIVES.has(step.name)) {
          seen.add(step.name);
          orderedPrimitives.push(step.name);
        }
      });
    });
    
    return orderedPrimitives;
  }, [timelineData]);

  // Fetch metrics for each primitive
  const primitiveQueries = useQuery<MetricsApiResponse[]>({
    queryKey: [QUERY_KEYS.POINT_IN_TIME_METRICS, website],
    queryFn: async (): Promise<MetricsApiResponse[]> => {
      if (!uniquePrimitives.length) return [];
      
      const service = new WebsiteViewService();
      const promises = uniquePrimitives.map(async (primitiveName): Promise<MetricsApiResponse> => {
        try {
          const response = await service.getPrimitivePointInTimeMetrics(website || '', primitiveName);
          return {
            namespace: primitiveName,
            data: response,
            hasData: true
          };
        } catch (error) {
          return {
            namespace: primitiveName,
            data: null,
            hasData: false
          };
        }
      });
      
      return Promise.all(promises);
    },
    enabled: !!website && uniquePrimitives.length > 0 && !timelineLoading,
    staleTime: CACHE_TIMES.STALE_TIME,
    cacheTime: CACHE_TIMES.CACHE_TIME,
    retry: 3
  });

  const isLoading = timelineLoading || primitiveQueries.isFetching;

  // Create primitive sections from API responses
  const primitiveSections: NamespaceGroup[] = React.useMemo(() => {
    if (!primitiveQueries.data) return [];

    return primitiveQueries.data
      .filter(({ hasData, data }: MetricsApiResponse) => hasData && data && data.metrics && data.metrics.length > 0)
      .map(({ namespace: primitiveName, data }: MetricsApiResponse) => {
        const metrics: Metric[] = (data?.metrics ?? []).map((metric) => ({
          name: metric.name || 'Metric',
          value: (metric.value == null || !isFinite(metric.value)) ? 'N/A' : metric.value.toString()
        }));
        
        return {
          primitiveName,
          metrics,
          hasData: true
        };
      });
  }, [primitiveQueries.data]);

  return {
    website,
    isLoading,
    primitiveSections
  };
};
